import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CalendarRange, CheckCircle2, DownloadCloud, FileSpreadsheet, MapPin, RotateCcw, Trash2, Upload, X } from 'lucide-react';
import { gpsMonthColumns, rideColumns } from '../components/columns';
import { CarSelect } from '../components/forms/EntitySelects';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, DetailItem, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar } from '../components/ui/FilterBar';
import { Checkbox, Input, Select } from '../components/ui/Input';
import { ConfirmDialog } from '../components/ui/Modal';
import { Tabs } from '../components/ui/Tabs';
import { useDeleteGPSMonth, useGPSImport, useGPSMonth, useGPSMonths, useGPSPreview, useGPSSync, useGPSSyncStatus, useRides } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage } from '../services/api';
import { toast } from '../store';
import type { ColumnMapping, GPSField, GPSFileSummary, GPSImportedMonth, GPSImportPreview, GPSImportResult, GPSMonth, GPSPreviewMonth, GPSPreviewRow, GPSRowError } from '../types';
import { cn, formatDate, formatDateTime, formatDuration, formatFileSize, formatKm, formatMonth, formatNumber } from '../utils/helpers';
import { t, tr, tx } from '../i18n';

const FIELD_LABELS: Record<GPSField, string> = tr({
  ride_date: 'Dátum jazdy',
  license_plate: 'ŠPZ vozidla',
  start_time: 'Čas začiatku',
  end_time: 'Čas konca',
  start_location: 'Odkiaľ',
  end_location: 'Kam',
  distance_km: 'Vzdialenosť (km)',
  duration_minutes: 'Doba jazdy (min)',
  driver: 'Vodič (meno)',
  purpose: 'Účel jazdy',
  external_id: 'Číslo jazdy',
  odometer_end: 'Stav tachometra',
});

const FORMAT_LABELS: Record<GPSFileSummary['format'], string> = tr({
  gpspoloha: 'GPSPOLOHA.SK – kniha jázd',
  generic: 'Všeobecná tabuľka',
  unknown: 'Nerozpoznaný',
});

export function GPS() {
  const [filters, setFilters] = useUrlFilters({ tab: 'months', month: '', car_id: '', period: '', page: '1' });
  const monthId = toInt(filters.month);

  if (monthId) return <MonthDetail id={monthId} onBack={() => setFilters({ month: '' })} />;
  return (
    <>
      <PageHeader title={t('GPS jazdy')} subtitle={t('Mesačné knihy jázd z GPS systému – jeden záznam za vozidlo a mesiac')} />
      <AutoSyncCard />
      <Tabs
        className="mb-4"
        value={filters.tab}
        onChange={(tab) => setFilters({ tab })}
        tabs={[
          { value: 'months', label: t('Mesačné záznamy') },
          { value: 'import', label: t('Nový import') },
        ]}
      />
      {filters.tab === 'import' ? (
        <ImportWizard onShowMonths={() => setFilters({ tab: 'months', car_id: '', period: '', page: '1' })} />
      ) : (
        <MonthsList filters={filters} setFilters={setFilters} />
      )}
    </>
  );
}

// ---------------------------------------------------------------- automatic download
function AutoSyncCard() {
  const { data: status } = useGPSSyncStatus();
  const sync = useGPSSync();
  if (!status) return null;

  const runNow = async () => {
    try {
      const result = await sync.mutateAsync();
      if (result.error) toast.error(result.error);
      else toast.success(t('Stiahnuté z GPS – vozidlá: {cars}, jazdy: {rides}', { cars: result.cars_updated, rides: result.rides }));
    } catch (err) {
      toast.error(errorMessage(err, t('Sťahovanie z GPS zlyhalo')));
    }
  };

  const last = status.last;
  const hours = status.hours.map((h) => `${String(h).padStart(2, '0')}:00`).join(t(' a '));
  return (
    <Card className="mb-4">
      <CardHeader
        className="mb-2"
        title={
          <span className="flex items-center gap-2">
            <DownloadCloud className="h-5 w-5 text-primary-600" />
            {t('Automatické sťahovanie z GPSPOLOHA')}
          </span>
        }
        subtitle={
          status.configured
            ? t('Každý deň o {hours} sa stiahnu jazdy, kilometre a poloha všetkých vozidiel.', { hours })
            : t('Nie je zapnuté – na serveri chýba premenná GPSPOLOHA_API_KEY.')
        }
        action={
          status.configured && (
            <Button variant="outline" size="sm" onClick={runNow} loading={sync.isPending || status.running}>
              <DownloadCloud className="h-4 w-4" />
              {t('Stiahnuť teraz')}
            </Button>
          )
        }
      />
      {last && (
        <div className="space-y-1 text-sm text-secondary-600">
          <p>
            {t('Naposledy:')} <strong className="text-secondary-900">{formatDateTime(last.finished_at ?? last.started_at)}</strong>
            {' · '}
            {last.trigger === 'manual' ? t('ručne') : t('automaticky')}
            {' · '}
            {t('aktualizované vozidlá: {cars} z {total}, jazdy: {rides}', { cars: last.cars_updated, total: last.cars_total, rides: last.rides })}
          </p>
          {last.error && (
            <p className="flex items-center gap-1.5 text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {last.error}
            </p>
          )}
          {last.not_found.length > 0 && (
            <p className="text-secondary-500">{t('Nie sú v GPSPOLOHA: {plates}', { plates: last.not_found.join(', ') })}</p>
          )}
          {last.errors.map((e) => (
            <p key={e.license_plate} className="text-amber-700">
              {e.license_plate}: {e.message}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- wizard
function ImportWizard({ onShowMonths }: { onShowMonths: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<GPSImportPreview | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [createMissing, setCreateMissing] = useState(true);
  const [result, setResult] = useState<GPSImportResult | null>(null);
  const previewMutation = useGPSPreview();
  const importMutation = useGPSImport();

  const reset = () => {
    setFiles([]);
    setPreview(null);
    setMapping(null);
    setResult(null);
  };

  const loadPreview = async (selected: File[], nextMapping?: ColumnMapping) => {
    if (!selected.length) return reset();
    try {
      const data = await previewMutation.mutateAsync({ files: selected, mapping: nextMapping });
      setPreview(data);
      setMapping(data.column_mapping);
    } catch (err) {
      toast.error(errorMessage(err, t('Súbory sa nepodarilo spracovať')));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.XLSX'] },
    multiple: true,
    onDrop: (accepted, rejected) => {
      if (rejected.length) toast.error(t('{rejected} súbor(ov) nie je vo formáte XLSX', { rejected: rejected.length }));
      if (!accepted.length) return;
      const byName = new Map([...files, ...accepted].map((f) => [f.name, f]));
      const next = [...byName.values()];
      setFiles(next);
      setResult(null);
      loadPreview(next, preview?.needs_mapping ? mapping ?? undefined : undefined);
    },
  });

  const removeFile = (name: string) => {
    const next = files.filter((f) => f.name !== name);
    setFiles(next);
    loadPreview(next, mapping ?? undefined);
  };

  const runImport = async () => {
    if (!files.length || !preview) return;
    try {
      const data = await importMutation.mutateAsync({
        files,
        mapping: preview.needs_mapping ? mapping ?? undefined : undefined,
        createMissing,
      });
      setResult(data);
      setPreview(null);
      if (data.months.length) toast.success(t('Importované mesačné záznamy: {months}', { months: data.months.length }));
      else toast.warning(t('Nebol importovaný žiadny mesačný záznam'));
    } catch (err) {
      toast.error(errorMessage(err, t('Import zlyhal')));
    }
  };

  if (result) {
    return (
      <Card>
        <div className="flex flex-col items-center py-6 text-center">
          <CheckCircle2 className={cn('mb-3 h-12 w-12', result.months.length ? 'text-green-500' : 'text-secondary-300')} />
          <h2 className="text-lg font-semibold">{t('Import dokončený')}</h2>
          <p className="text-secondary-500">{t('Súbory: {count}', { count: result.files.length })}</p>
          <div className="mt-6 grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('Mesačné záznamy')} value={result.months.length} tone="text-green-700" />
            <Stat label={t('Z toho nahradené')} value={result.months.filter((m) => m.replaced).length} tone="text-secondary-600" />
            <Stat label={t('Importované jazdy')} value={result.created} />
            <Stat label={t('Chybné riadky')} value={result.skipped_invalid} tone={result.skipped_invalid ? 'text-red-600' : undefined} />
          </div>
          {result.months.length > 0 && (
            <div className="mt-4 w-full max-w-3xl overflow-hidden rounded-lg border border-secondary-200 text-left">
              <DataTable data={result.months} columns={importedMonthColumns()} rowKey={(m) => m.id} />
            </div>
          )}
          {(result.created_cars.length > 0 || result.created_drivers.length > 0 || result.updated_km_cars.length > 0) && (
            <div className="mt-4 w-full max-w-2xl space-y-1 rounded-lg bg-amber-50 p-3 text-left text-sm text-amber-900">
              {result.created_cars.length > 0 && <p>{t('Novo založené vozidlá (doplňte značku a model): {cars}', { cars: result.created_cars.join(', ') })}</p>}
              {result.created_drivers.length > 0 && <p>{t('Novo založení vodiči: {drivers}', { drivers: result.created_drivers.join(', ') })}</p>}
              {result.updated_km_cars.length > 0 && <p>{t('Stav km aktualizovaný podľa tachometra: {cars}', { cars: result.updated_km_cars.join(', ') })}</p>}
            </div>
          )}
          <ErrorList errors={result.errors} />
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={reset}>
              <Upload className="h-4 w-4" />  {t('Importovať ďalšie súbory')}
            </Button>
            {result.months.length > 0 && <Button onClick={onShowMonths}>{t('Zobraziť mesačné záznamy')}</Button>}
          </div>
        </div>
      </Card>
    );
  }

  const dropzone = (
    <div
      {...getRootProps()}
      className={cn(
        'cursor-pointer rounded-xl border-2 border-dashed text-center transition-colors',
        files.length ? 'p-4' : 'p-10',
        isDragActive ? 'border-primary-500 bg-primary-50' : 'border-secondary-300 hover:border-primary-400',
      )}
    >
      <input {...getInputProps()} />
      {files.length ? (
        <p className="text-sm font-medium text-secondary-700">
          <Upload className="mr-1 inline h-4 w-4" />  {t('Pridať ďalšie súbory (pretiahnite sem alebo kliknite)')}
        </p>
      ) : (
        <>
          <FileSpreadsheet className="mx-auto mb-3 h-12 w-12 text-secondary-400" />
          <p className="font-medium text-secondary-700">{t('Pretiahnite sem mesačné knihy jázd (XLSX) alebo kliknite pre výber')}</p>
          <p className="mx-auto mt-2 max-w-xl text-sm text-secondary-500">
            {t(
              'Export „mesačná kniha jázd“ z GPSPOLOHA.SK (súbor KJ_…) – jeden súbor za vozidlo a mesiac, vozidlo sa rozpozná z hlavičky. Naraz môžete nahrať viac vozidiel aj mesiacov. Z každého vznikne jeden mesačný záznam s kilometrami a navštívenými miestami; opätovný import toho istého mesiaca ho nahradí. Pred importom uvidíte náhľad; nič sa neuloží, kým import nepotvrdíte.',
            )}
          </p>
        </>
      )}
    </div>
  );

  if (!files.length) return <Card>{dropzone}</Card>;

  const missingRequired =
    preview && mapping && preview.needs_mapping
      ? preview.required_fields.filter((f) => !mapping[f] && !(f === 'ride_date' && mapping.start_time))
      : [];
  const fileErrors = preview?.files.filter((f) => f.error) ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={t('1. Súbory')}
          subtitle={preview ? t('{files} súbor(ov) · {months} mesačných záznamov · {totalRows} jázd', { files: files.length, months: preview.months.length, totalRows: formatNumber(preview.total_rows) }) : t('Spracovávam…')}
          action={
            <Button variant="ghost" size="sm" onClick={reset}>
              <RotateCcw className="h-4 w-4" />  {t('Začať znova')}
            </Button>
          }
        />
        <ul className="mb-3 divide-y divide-secondary-100 rounded-lg border border-secondary-200">
          {files.map((file) => {
            const summary = preview?.files.find((f) => f.file_name === file.name);
            return (
              <li key={file.name} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <FileSpreadsheet className="h-4 w-4 shrink-0 text-green-600" />
                <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                {summary && (
                  <>
                    <Badge variant={summary.error ? 'danger' : summary.format === 'gpspoloha' ? 'primary' : 'default'}>
                      {FORMAT_LABELS[summary.format]}
                    </Badge>
                    {summary.license_plate && <span className="font-mono text-secondary-700">{summary.license_plate}</span>}
                    <span className="text-secondary-500">{summary.error ?? t('{rows} jázd', { rows: summary.rows })}</span>
                  </>
                )}
                <span className="text-xs text-secondary-400">{formatFileSize(file.size)}</span>
                <button className="icon-btn" onClick={() => removeFile(file.name)} aria-label={t('Odobrať {name}', { name: file.name })} disabled={previewMutation.isPending}>
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
        {dropzone}
      </Card>

      {preview && mapping && preview.needs_mapping && (
        <Card>
          <CardHeader
            title={t('Mapovanie stĺpcov')}
            subtitle={t('Pre súbory vo formáte bežnej tabuľky. Stĺpce sa rozpoznali automaticky – skontrolujte ich.')}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {preview.fields.map((field) => (
              <Select
                key={field}
                label={FIELD_LABELS[field]}
                required={preview.required_fields.includes(field)}
                value={mapping[field] ?? ''}
                placeholder={t('— nepoužiť —')}
                options={preview.headers.map((h) => ({ value: h, label: h }))}
                onChange={(e) => setMapping({ ...mapping, [field]: e.target.value || null })}
              />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" loading={previewMutation.isPending} onClick={() => loadPreview(files, mapping)}>
              {t('Aktualizovať náhľad')}
            </Button>
            {missingRequired.length > 0 && (
              <span className="text-sm text-red-600">{t('Chýbajú povinné stĺpce: {columns}', { columns: missingRequired.map((f) => FIELD_LABELS[f]).join(', ') })}</span>
            )}
          </div>
        </Card>
      )}

      {preview && (
        <Card>
          <h3 className="mb-3 text-base font-semibold text-secondary-900">{t('2. Kontrola údajov')}</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('Mesačné záznamy')} value={preview.months.length} tone="text-green-700" />
            <Stat label={t('Nahradia existujúce')} value={preview.months.filter((m) => m.replaces_existing).length} tone="text-secondary-600" />
            <Stat label={t('Jázd na import')} value={preview.valid_rows} />
            <Stat label={t('Chybné riadky')} value={preview.invalid_rows} tone={preview.invalid_rows ? 'text-red-600' : undefined} />
          </div>

          {fileErrors.length > 0 && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {fileErrors.map((f) => (
                <p key={f.file_name}>
                  <strong>{f.file_name}:</strong> {f.error}
                </p>
              ))}
            </div>
          )}

          {(preview.new_cars.length > 0 || preview.new_drivers.length > 0) && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />  {t('V súboroch sú vozidlá alebo vodiči, ktorí ešte nie sú v evidencii')}
              </p>
              {preview.new_cars.length > 0 && <p className="mt-1">{t('Vozidlá: {cars}', { cars: preview.new_cars.join(', ') })}</p>}
              {preview.new_drivers.length > 0 && <p className="mt-1">{t('Vodiči: {drivers}', { drivers: preview.new_drivers.join(', ') })}</p>}
              <Checkbox
                className="mt-2"
                checked={createMissing}
                onChange={(e) => setCreateMissing(e.target.checked)}
                label={t('Automaticky ich založiť (inak sa ich jazdy preskočia)')}
              />
            </div>
          )}

          <div className="mt-4 overflow-hidden rounded-lg border border-secondary-200">
            <DataTable
              data={preview.months}
              columns={previewMonthColumns()}
              rowKey={(m) => `${m.license_plate}-${m.year}-${m.month}`}
              emptyMessage={t('V súboroch sa nenašli žiadne jazdy')}
            />
          </div>
          {preview.months.some((m) => m.replaces_existing) && (
            <p className="mt-2 text-sm text-amber-800">
              {t('Mesiace označené „nahradí“ už v evidencii sú – ich doterajšie jazdy sa nahradia jazdami z nahraného súboru.')}
            </p>
          )}

          <details className="mt-4 overflow-hidden rounded-lg border border-secondary-200">
            <summary className="cursor-pointer bg-secondary-50 px-4 py-2 text-sm font-medium text-secondary-700">
              {t('Jazdy v súboroch ({count})', { count: formatNumber(preview.total_rows) })}
            </summary>
            <DataTable
              data={preview.preview_rows}
              columns={previewColumns(files.length > 1)}
              rowKey={(r) => `${r.file_name}-${r.row_index}`}
              rowClassName={(r) => (r.errors.length ? 'bg-red-50/60' : undefined)}
              emptyMessage={t('V súboroch sa nenašli žiadne jazdy')}
            />
            {preview.total_rows > preview.preview_rows.length && (
              <p className="border-t border-secondary-100 px-4 py-2 text-xs text-secondary-500">
                {t('Zobrazených prvých {shown} z {total} jázd.', { shown: preview.preview_rows.length, total: preview.total_rows })}
              </p>
            )}
          </details>
          <ErrorList errors={preview.errors} />
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {preview && (
          <span className="text-sm text-secondary-500">
            {t('Importuje sa {months} mesačných záznamov ({rides} jázd)', { months: preview.months.length, rides: formatNumber(preview.valid_rows) })}
            {preview.new_cars.length > 0 && !createMissing ? t(' bez neznámych vozidiel') : ''}.
          </span>
        )}
        <Button
          size="lg"
          onClick={runImport}
          loading={importMutation.isPending || previewMutation.isPending}
          disabled={!preview || missingRequired.length > 0 || preview.valid_rows === 0}
        >
          <Upload className="h-4 w-4" />  {t('Potvrdiť import')}
        </Button>
      </div>
    </div>
  );
}

function monthSummaryColumns<T extends GPSPreviewMonth | GPSImportedMonth>(status: Column<T>): Column<T>[] {
  return [
    { key: 'car', header: t('Vozidlo'), className: 'whitespace-nowrap font-mono font-medium', render: (m) => m.license_plate },
    { key: 'month', header: t('Mesiac'), className: 'whitespace-nowrap', render: (m) => formatMonth(m.year, m.month) },
    { key: 'rides', header: t('Jázd'), className: 'text-right tabular-nums', headerClassName: 'text-right', render: (m) => formatNumber(m.ride_count) },
    { key: 'km', header: t('Najazdené'), className: 'text-right tabular-nums whitespace-nowrap', headerClassName: 'text-right', render: (m) => formatKm(m.distance_km) },
    {
      key: 'places',
      header: t('Navštívené miesta'),
      render: (m) => (
        <p className="line-clamp-2 min-w-[12rem] max-w-sm text-xs" title={m.places ?? undefined}>
          {m.places || '–'}
        </p>
      ),
    },
    status,
  ];
}

const previewMonthColumns = () =>
  monthSummaryColumns<GPSPreviewMonth>({
  key: 'status',
  header: t('Stav'),
  className: 'whitespace-nowrap',
  render: (m) => (
    <>
      {m.replaces_existing ? <Badge variant="warning">{t('nahradí existujúci')}</Badge> : <Badge variant="success">{t('nový')}</Badge>}
      {!m.car_exists && <Badge variant="warning" className="ml-1">{t('nové vozidlo')}</Badge>}
    </>
  ),
});

const importedMonthColumns = () =>
  monthSummaryColumns<GPSImportedMonth>({
  key: 'status',
  header: t('Stav'),
  className: 'whitespace-nowrap',
  render: (m) => (
    <Link to={`/gps?month=${m.id}`} className="text-sm font-medium text-primary-700 hover:underline">
      {m.replaced ? t('Nahradené') : t('Nové')} – detail
    </Link>
  ),
});

function previewColumns(showFile: boolean): Column<GPSPreviewRow>[] {
  return [
    {
      key: 'row',
      header: showFile ? t('Súbor / riadok') : t('Riadok'),
      className: 'text-secondary-500 tabular-nums',
      render: (r) => (showFile ? <span className="block max-w-[10rem] truncate text-xs" title={r.file_name}>{r.file_name} · {r.row_index}</span> : r.row_index),
    },
    { key: 'date', header: t('Dátum'), className: 'whitespace-nowrap', render: (r) => formatDate(r.values.ride_date as string) },
    {
      key: 'time',
      header: t('Čas'),
      className: 'whitespace-nowrap',
      render: (r) => `${String(r.values.start_time ?? '').slice(0, 5) || '–'} – ${String(r.values.end_time ?? '').slice(0, 5) || '–'}`,
    },
    {
      key: 'car',
      header: t('ŠPZ'),
      render: (r) => (
        <span className="whitespace-nowrap font-mono">
          {r.values.license_plate ?? '–'}
          {r.values.license_plate && !r.car_exists && <Badge variant="warning" className="ml-1">{t('nové')}</Badge>}
        </span>
      ),
    },
    {
      key: 'driver',
      header: t('Vodič'),
      render: (r) => (
        <span className="whitespace-nowrap">
          {r.values.driver ?? '–'}
          {r.driver_exists === false && <Badge variant="warning" className="ml-1">{t('nový')}</Badge>}
        </span>
      ),
    },
    {
      key: 'route',
      header: t('Trasa'),
      render: (r) => (
        <span className="line-clamp-2 max-w-xs text-xs">
          {r.values.start_location ?? '–'} → {r.values.end_location ?? '–'}
        </span>
      ),
    },
    { key: 'km', header: t('Km'), className: 'text-right tabular-nums', render: (r) => formatNumber(r.values.distance_km as number, 1) },
    { key: 'number', header: t('Číslo jazdy'), className: 'whitespace-nowrap font-mono text-xs', render: (r) => r.values.external_id ?? '–' },
    {
      key: 'status',
      header: t('Stav'),
      render: (r) => (r.errors.length ? <span className="text-xs text-red-700">{r.errors.join('; ')}</span> : <Badge variant="success">{t('OK')}</Badge>),
    },
  ];
}

function Stat({ label, value, tone, digits = 0 }: { label: string; value: number; tone?: string; digits?: number }) {
  return (
    <div className="rounded-lg bg-secondary-50 p-3 text-center">
      <p className={cn('text-2xl font-bold tabular-nums', tone ?? 'text-secondary-900')}>{formatNumber(value, digits)}</p>
      <p className="text-xs text-secondary-500">{label}</p>
    </div>
  );
}

function ErrorList({ errors }: { errors: GPSRowError[] }) {
  if (!errors.length) return null;
  return (
    <details className="mt-4 w-full max-w-3xl self-center rounded-lg border border-red-200 bg-red-50 p-3 text-left text-sm text-red-800">
      <summary className="cursor-pointer font-medium">Chyby ({errors.length})</summary>
      <ul className="mt-2 max-h-48 space-y-0.5 overflow-y-auto">
        {errors.map((e, i) => (
          <li key={`${e.file_name}-${e.row}-${i}`}>
            {e.file_name}
            {e.row ? `, riadok ${e.row}` : ''}: {e.errors.join('; ')}
          </li>
        ))}
      </ul>
    </details>
  );
}

// ---------------------------------------------------------------- monthly records
type MonthFilterValues = { tab: string; month: string; car_id: string; period: string; page: string };

function MonthsList({ filters, setFilters }: { filters: MonthFilterValues; setFilters: (patch: Partial<MonthFilterValues>) => void }) {
  const page = toInt(filters.page, 1)!;
  const [year, month] = filters.period ? filters.period.split('-').map(Number) : [undefined, undefined];
  const { data, isLoading, isFetching } = useGPSMonths({ car_id: toInt(filters.car_id), year, month, page, page_size: 50 });
  const [deleting, setDeleting] = useState<GPSMonth | null>(null);

  return (
    <Card padding="none">
      <FilterBar>
        <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-64" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value })} />
        <Input label={t('Mesiac')} type="month" wrapperClassName="sm:w-48" value={filters.period} onChange={(e) => setFilters({ period: e.target.value })} />
      </FilterBar>
      {data && (
        <p className="border-b border-secondary-100 px-4 py-2 text-sm text-secondary-600">
          {tx('{count} mesačných záznamov · spolu {sum}', { count: formatNumber(data.total), sum: <strong>{formatKm(data.total_distance_km)}</strong> })}
        </p>
      )}
      <DataTable
        data={data?.months}
        columns={gpsMonthColumns({ showCar: true, onDelete: setDeleting })}
        rowKey={(m) => m.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={
          <EmptyState icon={<CalendarRange className="h-10 w-10" />} title={t('Žiadne mesačné záznamy')}>
            
            {t('Nahrajte mesačnú knihu jázd v záložke „Nový import“.')}
          </EmptyState>
        }
        pagination={data && { page, pageSize: 50, total: data.total, onPageChange: (p) => setFilters({ page: String(p) }) }}
      />
      <DeleteMonthDialog record={deleting} onClose={() => setDeleting(null)} />
    </Card>
  );
}

function DeleteMonthDialog({ record, onClose, onDeleted }: { record: GPSMonth | null; onClose: () => void; onDeleted?: () => void }) {
  const remove = useDeleteGPSMonth();
  return (
    <ConfirmDialog
      isOpen={!!record}
      onClose={onClose}
      loading={remove.isPending}
      title={t('Zmazať mesačný záznam')}
      confirmText={t('Zmazať')}
      message={
        record &&
        t('Zmazať záznam {plate} za {month} so všetkými {rideCount} jazdami? Mesiac potom môžete importovať znova.', { plate: record.car?.license_plate, month: formatMonth(record.year, record.month), rideCount: record.ride_count })
      }
      onConfirm={async () => {
        try {
          const res = await remove.mutateAsync(record!.id);
          toast.success(t('Zmazaný mesačný záznam ({deleted} jázd)', { deleted: res.deleted }));
          onDeleted?.();
        } catch (err) {
          toast.error(errorMessage(err));
        }
        onClose();
      }}
    />
  );
}

function MonthDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [deleting, setDeleting] = useState<GPSMonth | null>(null);
  const { data: record, isLoading, error } = useGPSMonth(id);
  const { data: rides, isLoading: ridesLoading, isFetching } = useRides({ month_id: id, page, page_size: 100 });

  const back = (
    <Button variant="ghost" size="sm" onClick={onBack}>
      <ArrowLeft className="h-4 w-4" />  {t('Mesačné záznamy')}
    </Button>
  );
  if (error) {
    return (
      <Card>
        {back}
        <EmptyState icon={<CalendarRange className="h-10 w-10" />} title={t('Mesačný záznam sa nenašiel')} />
      </Card>
    );
  }

  return (
    <>
      <div className="mb-2">{back}</div>
      <PageHeader
        title={record ? `${record.car?.license_plate} – ${formatMonth(record.year, record.month)}` : t('Načítava sa…')}
        subtitle={record && t('{brand} {model} · mesačná kniha jázd', { brand: record.car?.brand, model: record.car?.model })}
        actions={
          record && (
            <Button variant="outline" onClick={() => setDeleting(record)}>
              <Trash2 className="h-4 w-4" />  {t('Zmazať mesiac')}
            </Button>
          )
        }
      />
      {record && (
        <Card className="mb-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('Najazdené km')} value={record.distance_km} digits={1} tone="text-primary-700" />
            <Stat label={t('Služobne km')} value={record.business_km} digits={1} />
            <Stat label={t('Súkromne km')} value={record.private_km} digits={1} />
            <Stat label={t('Jázd')} value={record.ride_count} />
          </div>
          <dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <DetailItem label={t('Tachometer')}>
              {record.odometer_end !== null ? `${formatNumber(record.odometer_start, 1)} → ${formatNumber(record.odometer_end, 1)} km` : '–'}
            </DetailItem>
            <DetailItem label={t('Čas jazdy')}>{formatDuration(record.driving_minutes)}</DetailItem>
            <DetailItem label={t('Importované')}>
              {formatDateTime(record.imported_at)}
              {record.importer && ` · ${record.importer.full_name || record.importer.username}`}
              {record.source_files && <span className="block text-xs text-secondary-500">{record.source_files}</span>}
            </DetailItem>
          </dl>
          <div className="mt-5">
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{t('Navštívené miesta (od najčastejších)')}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {record.places ? record.places.split(', ').map((place) => <Badge key={place}>{place}</Badge>) : '–'}
            </div>
          </div>
        </Card>
      )}
      <Card padding="none">
        <div className="p-4">
          <h2 className="font-semibold">{t('Jazdy v mesiaci')}</h2>
        </div>
        <DataTable
          data={rides?.rides}
          columns={rideColumns(false)}
          rowKey={(r) => r.id}
          isLoading={isLoading || ridesLoading}
          isFetching={isFetching}
          emptyMessage={<EmptyState icon={<MapPin className="h-10 w-10" />} title={t('Žiadne jazdy')} />}
          pagination={rides && { page, pageSize: 100, total: rides.total, onPageChange: setPage }}
        />
      </Card>
      <DeleteMonthDialog record={deleting} onClose={() => setDeleting(null)} onDeleted={() => navigate('/gps', { replace: true })} />
    </>
  );
}
